/**
 * registry.ts
 *
 * Module template registry. Defines the Learn modules and their
 * default configurations. The Learn page reads from this to render
 * the module library.
 */

import { ModuleTemplate } from '../types/module';

/* ------------------------------------------------------------------ */
/*  Placeholder helpers                                                */
/* ------------------------------------------------------------------ */

/**
 * Default configurable-fields shape for `available: false` placeholder
 * modules. They aren't launchable yet, so nothing is configurable.
 */
const PLACEHOLDER_CONFIGURABLE = {
  key: false,
  scale: false,
  bpm: false,
  progression: false,
  position: false,
  loop: false,
  metronome: false,
  barsPerChord: false,
  chordQuality: false,
  triadGroup: false,
  inversion: false,
};

const TRIAD_SHAPE_CONFIGURABLE = {
  ...PLACEHOLDER_CONFIGURABLE,
  key: true,
  bpm: true,
  metronome: true,
  loop: true,
};
const SCALE_SHAPE_CONFIGURABLE = { ...TRIAD_SHAPE_CONFIGURABLE, flavor: true };

export type TriadQuality = 'major' | 'minor' | 'augmented' | 'diminished';

export const TRIAD_STRING_SETS = [
  { slug: 'ead', label: 'E-A-D' },
  { slug: 'adg', label: 'A-D-G' },
  { slug: 'dgb', label: 'D-G-B' },
  { slug: 'gbe', label: 'G-B-e' },
] as const;

const TRIAD_QUALITY_COPY: Record<
  TriadQuality,
  {
    label: string;
    idPrefix: string;
    difficulty: ModuleTemplate['difficulty'];
    description: (set: string) => string;
    valueProposition: (set: string) => string;
    actionLabel: (set: string) => string;
    intervalOutcome: string;
  }
> = {
  major: {
    label: 'Major',
    idPrefix: '',
    difficulty: 'starter',
    description: (set) =>
      `Cycle every major triad voicing on the ${set} string set — root position and both inversions — on a metronome-timed loop.`,
    valueProposition: (set) =>
      `Build muscle memory for ${set} triads before drilling identification or inversions.`,
    actionLabel: (set) => `Drill ${set} shapes`,
    intervalOutcome: 'Major (1-3-5) intervals',
  },
  minor: {
    label: 'Minor',
    idPrefix: 'min-',
    difficulty: 'starter',
    description: (set) =>
      `Cycle every minor triad voicing on the ${set} string set on a metronome-timed loop.`,
    valueProposition: (set) =>
      `Build muscle memory for ${set} minor triads alongside the major shapes.`,
    actionLabel: (set) => `Drill ${set} minor shapes`,
    intervalOutcome: 'Minor (1-\u266D3-5) intervals',
  },
  augmented: {
    label: 'Augmented',
    idPrefix: 'aug-',
    difficulty: 'intermediate',
    description: (set) =>
      `Cycle every augmented triad voicing on the ${set} string set on a metronome-timed loop.`,
    valueProposition: (set) =>
      `Hear the symmetry of augmented triads under your fingers on ${set}.`,
    actionLabel: (set) => `Drill ${set} augmented shapes`,
    intervalOutcome: 'Augmented (1-3-#5) intervals',
  },
  diminished: {
    label: 'Diminished',
    idPrefix: 'dim-',
    difficulty: 'intermediate',
    description: (set) =>
      `Cycle every diminished triad voicing on the ${set} string set on a metronome-timed loop.`,
    valueProposition: (set) =>
      `Master the tension of diminished triads (1-\u266D3-\u266D5) on ${set}.`,
    actionLabel: (set) => `Drill ${set} diminished shapes`,
    intervalOutcome: 'Diminished (1-\u266D3-\u266D5) intervals',
  },
};

export function triadModuleId(
  quality: TriadQuality,
  groupIndex: number
): string {
  return `triad-shapes-${TRIAD_QUALITY_COPY[quality].idPrefix}${TRIAD_STRING_SETS[groupIndex].slug}`;
}

/** Quality × string-set triad modules (Pro). */
function buildTriadShapeModules(): ModuleTemplate[] {
  return (Object.keys(TRIAD_QUALITY_COPY) as TriadQuality[]).flatMap(
    (quality) => {
      const copy = TRIAD_QUALITY_COPY[quality];
      return TRIAD_STRING_SETS.map(({ label: set }, groupIndex) => {
        const id = triadModuleId(quality, groupIndex);
        return {
          id,
          learnFamily: 'triads' as const,
          title: `${copy.label} Triads \u2014 String Set ${
            groupIndex + 1
          } (${set})`,
          description: copy.description(set),
          valueProposition: copy.valueProposition(set),
          primaryActionLabel: copy.actionLabel(set),
          estimatedMinutes: 8,
          difficulty: copy.difficulty,
          outcomes: [
            `All 12 keys on ${set}`,
            'Root, 1st, and 2nd inversions',
            copy.intervalOutcome,
          ],
          icon: 'MusicNote',
          category: 'practice' as const,
          learnSection: 'fundamentals' as const,
          mode: 'chord_switch' as const,
          configRoute: `/learn/${id}`,
          available: true,
          requiredEntitlement: 'paid',
          defaults: {
            key: 'G' as const,
            scaleName: 'Major (Ionian)',
            bpm: 0,
            barsPerChord: 0,
            chordQuality: quality,
            triadGroupIndex: groupIndex,
            inversion: 0,
          },
          configurable: TRIAD_SHAPE_CONFIGURABLE,
        };
      });
    }
  );
}

/* ------------------------------------------------------------------ */
/*  Module Templates                                                   */
/* ------------------------------------------------------------------ */

export const MODULE_TEMPLATES: ModuleTemplate[] = [
  ...buildTriadShapeModules(),
  {
    id: 'guided-chord-progression',
    learnFamily: 'progressions',
    learnOrder: 1,
    title: 'Chord Progression',
    description:
      'Start from musical presets, edit chords and voicings, then follow a live playhead while shapes light up on the fretboard.',
    valueProposition:
      'Make harmony practical by seeing each chord as something playable right now.',
    primaryActionLabel: 'Build progressions',
    estimatedMinutes: 12,
    difficulty: 'intermediate',
    outcomes: [
      'Preset song shapes',
      'Editable chord timeline',
      'Saved progression configs',
    ],
    icon: 'QueueMusic',
    category: 'create',
    learnSection: 'jam-sessions',
    mode: 'guided_solo',
    configRoute: '/learn/chord-progression',
    available: true,
    requiredEntitlement: 'free',
    defaults: {
      key: 'C',
      scaleName: 'Major (Ionian)',
      bpm: 90,
      barsPerChord: 2,
      progressionId: 'i-iv-v-i',
    },
    configurable: {
      key: true,
      scale: false,
      bpm: true,
      progression: true,
      position: false,
      loop: true,
      metronome: true,
      barsPerChord: true,
      chordQuality: false,
      triadGroup: false,
      inversion: false,
    },
  },
  {
    id: 'tab-practice',
    learnFamily: 'tabs',
    learnOrder: 1,
    title: 'Tab practice',
    description:
      'Follow your saved tab passages on your guitar with current and upcoming note guidance.',
    valueProposition: 'Spend less time translating tab into finger positions.',
    primaryActionLabel: 'Try tab practice',
    difficulty: 'all-levels',
    icon: 'QueueMusic',
    category: 'play',
    learnSection: 'songs',
    mode: 'tab_follow',
    configRoute: '/learn/tab-practice',
    available: true,
    requiredEntitlement: 'free',
    usesAudioInput: false,
    defaults: {
      key: 'C',
      scaleName: 'Major (Ionian)',
      bpm: 80,
      barsPerChord: 0,
    },
    configurable: { ...PLACEHOLDER_CONFIGURABLE },
  },
  {
    id: 'guided-solo',
    learnFamily: 'soloing',
    learnOrder: 1,
    title: 'Guided Solo',
    description:
      'Solo over changing chords with note tiers, a drone, generated riff prompts, and a rolling cue strip.',
    valueProposition:
      'Practice note choice over real harmony, not just scale wandering.',
    primaryActionLabel: 'Start soloing',
    estimatedMinutes: 12,
    difficulty: 'advanced',
    outcomes: ['Chord-tone guidance', 'Riff prompts', 'Drone and transport'],
    icon: 'QueueMusic',
    category: 'play',
    learnSection: 'jam-sessions',
    mode: 'guided_solo',
    configRoute: '/learn/guided-solo',
    available: true,
    requiredEntitlement: 'paid',
    defaults: {
      key: 'C',
      scaleName: 'Major (Ionian)',
      bpm: 90,
      barsPerChord: 2,
      progressionId: 'i-vi-iv-v',
    },
    configurable: {
      key: true,
      scale: false,
      bpm: true,
      progression: true,
      position: false,
      loop: true,
      metronome: true,
      barsPerChord: true,
      chordQuality: false,
      triadGroup: false,
      inversion: false,
    },
  },
  /* ================================================================ */
  /*  PLACEHOLDERS — coming soon                                       */
  /*                                                                   */
  /*  These describe the future Learn surface so the page can render   */
  /*  the new tabbed Fundamentals / Exercises / Jam Sessions / Songs   */
  /*  structure today. They are `available: false` and render with a   */
  /*  "Coming soon" treatment (no nav, no progress, no entitlement).   */
  /* ================================================================ */

  // ---- Fundamentals -------------------------------------------------
  {
    id: 'fretboard-notes',
    title: 'Fretboard Notes',
    description:
      'Memory game for finding notes anywhere on the neck. Call-and-respond rounds, no clock, accuracy-first.',
    valueProposition: 'Stop hunting for notes — know the fretboard cold.',
    primaryActionLabel: 'Coming soon',
    estimatedMinutes: 5,
    difficulty: 'starter',
    outcomes: ['Note recognition', 'Per-string drills', 'Accuracy tracking'],
    icon: 'MusicNote',
    category: 'practice',
    learnSection: 'fundamentals',
    mode: 'scale_practice',
    configRoute: '/learn/fretboard-notes',
    available: false,
    requiredEntitlement: 'free',
    defaults: { key: 'C', scaleName: 'Chromatic', bpm: 0, barsPerChord: 0 },
    configurable: PLACEHOLDER_CONFIGURABLE,
  },
  {
    id: 'triad-tones-quiz',
    title: 'Triad Tones Quiz',
    description:
      'Play the root, third, fifth, or a chosen inversion when prompted. Builds chord-tone awareness across the neck.',
    valueProposition:
      'Hear "the 3rd" and know exactly where to put your finger.',
    primaryActionLabel: 'Coming soon',
    estimatedMinutes: 6,
    difficulty: 'all-levels',
    outcomes: ['Root / 3 / 5 prompts', 'Inversion drills', 'Accuracy tracking'],
    icon: 'MusicNote',
    category: 'practice',
    learnSection: 'fundamentals',
    mode: 'chord_switch',
    configRoute: '/learn/triad-tones-quiz',
    available: false,
    requiredEntitlement: 'free',
    defaults: {
      key: 'C',
      scaleName: 'Major (Ionian)',
      bpm: 0,
      barsPerChord: 0,
    },
    configurable: PLACEHOLDER_CONFIGURABLE,
  },
  {
    id: 'major-pentatonic-scale',
    learnFamily: 'pentatonic',
    learnOrder: 1,
    title: 'Pentatonic Scale',
    description:
      'Walk all five pentatonic positions on a metronome-timed loop. Choose the tempo and how many bars to stay on each position.',
    valueProposition:
      'Connect the pentatonic positions up the neck as one system.',
    primaryActionLabel: 'Practice scale',
    estimatedMinutes: 6,
    difficulty: 'starter',
    outcomes: [
      'All five positions',
      'Timed auto-advance',
      'Major or minor, any key',
    ],
    icon: 'LinearScale',
    category: 'practice',
    learnSection: 'fundamentals',
    mode: 'scale_practice',
    configRoute: '/learn/major-pentatonic-scale',
    available: true,
    requiredEntitlement: 'free',
    defaults: {
      key: 'G',
      scaleName: 'Major Pentatonic',
      bpm: 70,
      barsPerChord: 4,
      position: 1,
      flavor: 'major' as const,
    },
    configurable: SCALE_SHAPE_CONFIGURABLE,
  },
  {
    id: 'blues-scale-all',
    learnFamily: 'blues',
    learnOrder: 1,
    title: 'Blues Scale',
    description:
      'Walk all five blues scale positions on a metronome-timed loop. Choose the tempo and how many bars to stay on each position.',
    valueProposition: 'Connect the blues positions up the neck as one system.',
    primaryActionLabel: 'Practice scale',
    estimatedMinutes: 6,
    difficulty: 'starter',
    outcomes: [
      'All five positions',
      'Timed auto-advance',
      'Major or minor, any key',
    ],
    icon: 'LinearScale',
    category: 'practice',
    learnSection: 'fundamentals',
    mode: 'scale_practice',
    configRoute: '/learn/blues-scale-all',
    available: true,
    requiredEntitlement: 'paid',
    defaults: {
      key: 'G',
      scaleName: 'Major Blues',
      bpm: 70,
      barsPerChord: 4,
      position: 1,
      flavor: 'major',
    },
    configurable: SCALE_SHAPE_CONFIGURABLE,
  },
  {
    id: 'diatonic-scale-all',
    learnFamily: 'diatonic',
    learnOrder: 1,
    title: 'Major & Minor Scales',
    description:
      'Walk all seven major/minor scale positions on a metronome-timed loop. Choose the tempo and how many bars to stay on each position.',
    valueProposition:
      'Connect the seven diatonic positions up the neck as one system.',
    primaryActionLabel: 'Practice scale',
    estimatedMinutes: 8,
    difficulty: 'starter',
    outcomes: [
      'All seven positions',
      'Timed auto-advance',
      'Major or minor, any key',
    ],
    icon: 'LinearScale',
    category: 'practice',
    learnSection: 'fundamentals',
    mode: 'scale_practice',
    configRoute: '/learn/diatonic-scale-all',
    available: true,
    requiredEntitlement: 'paid',
    defaults: {
      key: 'G',
      scaleName: 'Major Scale',
      bpm: 70,
      barsPerChord: 4,
      position: 1,
      flavor: 'major',
    },
    configurable: SCALE_SHAPE_CONFIGURABLE,
  },

  // ---- Exercises ----------------------------------------------------
  {
    id: 'arpeggios',
    title: 'Arpeggios',
    description:
      'Common arpeggio shapes (maj7, m7, dom7, dim) with the parent scale faded in context. Enter your clean BPM after each set.',
    valueProposition: 'Connect chord tones to scales — not isolated shapes.',
    primaryActionLabel: 'Coming soon',
    estimatedMinutes: 8,
    difficulty: 'intermediate',
    outcomes: ['Shape library', 'In-scale overlay', 'BPM tracking'],
    icon: 'LinearScale',
    category: 'practice',
    learnSection: 'exercises',
    mode: 'scale_practice',
    configRoute: '/learn/arpeggios',
    available: false,
    requiredEntitlement: 'free',
    defaults: {
      key: 'A',
      scaleName: 'Minor Pentatonic',
      bpm: 80,
      barsPerChord: 0,
    },
    configurable: PLACEHOLDER_CONFIGURABLE,
  },
  {
    id: 'intervals',
    title: 'Intervals',
    description:
      'Move through 3rds, 6ths, octaves, and other interval pairs across positions. Targets clean execution at user-entered BPM.',
    valueProposition: 'Train the ear and the hand together.',
    primaryActionLabel: 'Coming soon',
    estimatedMinutes: 7,
    difficulty: 'intermediate',
    outcomes: ['3rds / 6ths / octaves', 'Per-position drills', 'BPM tracking'],
    icon: 'LinearScale',
    category: 'practice',
    learnSection: 'exercises',
    mode: 'scale_practice',
    configRoute: '/learn/intervals',
    available: false,
    requiredEntitlement: 'free',
    defaults: {
      key: 'C',
      scaleName: 'Major (Ionian)',
      bpm: 80,
      barsPerChord: 0,
    },
    configurable: PLACEHOLDER_CONFIGURABLE,
  },
  {
    id: 'custom-exercises',
    title: 'Custom Exercises',
    description:
      'Build your own pattern, save it with a name, and track your best clean BPM over time.',
    valueProposition: 'Drill the lick that\u2019s actually slowing you down.',
    primaryActionLabel: 'Coming soon',
    estimatedMinutes: 5,
    difficulty: 'all-levels',
    outcomes: ['User-defined patterns', 'Named drills', 'BPM tracking'],
    icon: 'LinearScale',
    category: 'create',
    learnSection: 'exercises',
    mode: 'scale_practice',
    configRoute: '/learn/custom-exercises',
    available: false,
    requiredEntitlement: 'free',
    defaults: {
      key: 'C',
      scaleName: 'Major (Ionian)',
      bpm: 80,
      barsPerChord: 0,
    },
    configurable: PLACEHOLDER_CONFIGURABLE,
  },

  // ---- Jam Sessions (future additions) ------------------------------
  {
    id: 'play-along-loops',
    title: 'Loop Library',
    description:
      'Pick a backing loop, set the key/scale overlay, and jam. Saved loops remember last-played and overlay state.',
    valueProposition: 'Always have something to play over.',
    primaryActionLabel: 'Coming soon',
    estimatedMinutes: 10,
    difficulty: 'all-levels',
    outcomes: ['Backing loops', 'Scale overlay', 'Recently played'],
    icon: 'QueueMusic',
    category: 'play',
    learnSection: 'jam-sessions',
    mode: 'guided_solo',
    configRoute: '/learn/loops',
    available: false,
    requiredEntitlement: 'trial',
    defaults: {
      key: 'A',
      scaleName: 'Minor Pentatonic',
      bpm: 90,
      barsPerChord: 4,
    },
    configurable: PLACEHOLDER_CONFIGURABLE,
  },

  // ---- Songs --------------------------------------------------------
  {
    id: 'my-songs',
    title: 'My Songs',
    description:
      'Save and name your custom configs as songs. Flip between scale and chord overlays, see when you last played each one.',
    valueProposition:
      'Your repertoire, one tap away \u2014 with the overlay you actually want.',
    primaryActionLabel: 'Coming soon',
    estimatedMinutes: 0,
    difficulty: 'all-levels',
    outcomes: [
      'Named song configs',
      'Chord \u2194 scale toggle',
      'Last played tracking',
    ],
    icon: 'QueueMusic',
    category: 'create',
    learnSection: 'songs',
    mode: 'guided_solo',
    configRoute: '/learn/songs',
    available: false,
    requiredEntitlement: 'free',
    defaults: {
      key: 'C',
      scaleName: 'Major (Ionian)',
      bpm: 90,
      barsPerChord: 2,
    },
    configurable: PLACEHOLDER_CONFIGURABLE,
  },
  {
    id: 'riff-library',
    title: 'Riff Library',
    description:
      'Record what you play as tab snippets and save them to your library. Replay overlays in the context of any scale.',
    valueProposition: 'Capture the riff before it disappears.',
    primaryActionLabel: 'Coming soon',
    estimatedMinutes: 0,
    difficulty: 'all-levels',
    outcomes: ['Riff capture', 'Tab playback', 'In-scale overlay'],
    icon: 'MusicNote',
    category: 'create',
    learnSection: 'songs',
    mode: 'guided_solo',
    configRoute: '/learn/riffs',
    available: false,
    requiredEntitlement: 'paid',
    defaults: {
      key: 'C',
      scaleName: 'Major (Ionian)',
      bpm: 0,
      barsPerChord: 0,
    },
    configurable: PLACEHOLDER_CONFIGURABLE,
  },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

export function getModuleById(id: string): ModuleTemplate | undefined {
  return MODULE_TEMPLATES.find((m) => m.id === id);
}

export function getAvailableModules(): ModuleTemplate[] {
  return MODULE_TEMPLATES.filter((m) => m.available);
}
